/**
 * PDF Processor Utility
 * Uses pdf.js to convert PDF pages to images for flipbook display
 */

import * as pdfjsLib from "pdfjs-dist";

// Configure pdf.js worker - use unpkg which has all versions
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export interface ProcessedPage {
  pageNumber: number;
  blob: Blob;
  width: number;
  height: number;
}

export interface PDFProcessingResult {
  pages: ProcessedPage[];
  totalPages: number;
}

export interface PDFProcessingProgress {
  currentPage: number;
  totalPages: number;
  percent: number;
}

/**
 * Process a PDF file and convert each page to a WebP image
 * @param file - The PDF file to process
 * @param onProgress - Optional callback for progress updates
 * @param scale - Scale factor for rendering (default 2 for high quality)
 */
export async function processPDF(
  file: File,
  onProgress?: (progress: PDFProcessingProgress) => void,
  scale: number = 2,
): Promise<PDFProcessingResult> {
  // Load the PDF
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const totalPages = pdf.numPages;
  const pages: ProcessedPage[] = [];

  // Process each page
  for (let i = 1; i <= totalPages; i++) {
    // Report progress
    if (onProgress) {
      onProgress({
        currentPage: i,
        totalPages,
        percent: Math.round((i / totalPages) * 100),
      });
    }

    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });

    // Create canvas for rendering
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Could not get canvas context");
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Render the page
    await page.render({
      canvasContext: context,
      viewport,
    }).promise;

    // Convert to WebP blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error(`Failed to convert page ${i} to image`));
          }
        },
        "image/webp",
        0.9, // Quality
      );
    });

    pages.push({
      pageNumber: i,
      blob,
      width: viewport.width,
      height: viewport.height,
    });

    // Clean up
    canvas.remove();
  }

  return {
    pages,
    totalPages,
  };
}

/**
 * Get the first page of a PDF as an image for thumbnail
 */
export async function getPDFThumbnail(
  file: File,
  scale: number = 0.5,
): Promise<Blob> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Could not get canvas context");
  }

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({
    canvasContext: context,
    viewport,
  }).promise;

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        canvas.remove();
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create thumbnail"));
        }
      },
      "image/webp",
      0.8,
    );
  });
}

/**
 * Get the number of pages in a PDF without fully processing it
 */
export async function getPDFPageCount(file: File): Promise<number> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  return pdf.numPages;
}
